import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

export interface GroupedSelectItem {
  value: string
  label: string
  icon?: string
}

export interface GroupedSelectGroup {
  label: string
  items: GroupedSelectItem[]
  collapsed?: boolean
}

@customElement('grouped-select')
export class GroupedSelect extends LitElement {
  static override styles = css`
    :host {
      display: block;
      position: relative;
    }

    .select-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 8px;
      color: white;
      font-size: 13px;
      padding: 8px 12px;
      cursor: pointer;
      width: 100%;
      box-sizing: border-box;
      transition: border-color 0.15s ease;
    }

    .select-trigger:hover {
      border-color: rgba(255, 255, 255, 0.5);
    }

    .select-trigger.open {
      border-color: rgba(255, 255, 255, 0.7);
    }

    .trigger-content {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .trigger-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      flex-shrink: 0;
    }

    .trigger-label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 11px;
      position: absolute;
      top: -6px;
      left: 10px;
      background: rgba(30, 41, 59, 0.95);
      padding: 0 4px;
      font-weight: 500;
    }

    .trigger-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .trigger-placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .chevron {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      color: rgba(255, 255, 255, 0.7);
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    .chevron.open {
      transform: rotate(180deg);
    }

    .dropdown-panel {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: rgba(30, 41, 59, 0.98);
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      max-height: 240px;
      overflow-y: auto;
      z-index: 100;
      padding: 4px 0;
    }

    .group-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 12px;
      cursor: pointer;
      user-select: none;
      transition: background 0.1s ease;
    }

    .group-header:hover {
      background: rgba(255, 255, 255, 0.06);
    }

    .group-label {
      color: rgba(255, 255, 255, 0.5);
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .group-chevron {
      font-family: 'Material Symbols Outlined';
      font-size: 16px;
      color: rgba(255, 255, 255, 0.4);
      transition: transform 0.2s ease;
    }

    .group-chevron.collapsed {
      transform: rotate(-90deg);
    }

    .option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px 8px 16px;
      cursor: pointer;
      transition: background 0.1s ease;
      color: rgba(255, 255, 255, 0.85);
      font-size: 13px;
    }

    .option:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .option.selected {
      background: rgba(255, 255, 255, 0.15);
      color: white;
    }

    .option-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      width: 24px;
      text-align: center;
      flex-shrink: 0;
    }

    .option-placeholder-icon {
      width: 24px;
      text-align: center;
      flex-shrink: 0;
      color: rgba(255, 255, 255, 0.3);
      font-size: 14px;
    }

    .ungrouped-divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      margin: 4px 0;
    }
  `

  @property({ type: String })
  label = ''

  @property({ type: String })
  value = ''

  @property({ type: String })
  placeholder = 'Select...'

  @property({ type: Array })
  groups: GroupedSelectGroup[] = []

  @property({ type: Array })
  ungroupedItems: GroupedSelectItem[] = []

  @state()
  private open = false

  @state()
  private collapsedGroups: Set<string> = new Set()

  override connectedCallback(): void {
    super.connectedCallback()
    this._outsideClickHandler = this._handleOutsideClick.bind(this)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener('click', this._outsideClickHandler!, true)
  }

  private _outsideClickHandler?: (e: MouseEvent) => void

  private _handleOutsideClick(e: MouseEvent): void {
    const path = e.composedPath()
    if (!path.includes(this)) {
      this.open = false
      document.removeEventListener('click', this._outsideClickHandler!, true)
    }
  }

  private toggleOpen(): void {
    this.open = !this.open
    if (this.open) {
      document.addEventListener('click', this._outsideClickHandler!, true)
    } else {
      document.removeEventListener('click', this._outsideClickHandler!, true)
    }
  }

  private toggleGroup(groupLabel: string): void {
    const next = new Set(this.collapsedGroups)
    if (next.has(groupLabel)) {
      next.delete(groupLabel)
    } else {
      next.add(groupLabel)
    }
    this.collapsedGroups = next
  }

  private selectItem(item: GroupedSelectItem): void {
    this.value = item.value
    this.open = false
    document.removeEventListener('click', this._outsideClickHandler!, true)
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        detail: { value: item.value, label: item.label, icon: item.icon },
        bubbles: true,
      })
    )
  }

  private getSelectedItem(): GroupedSelectItem | undefined {
    for (const item of this.ungroupedItems) {
      if (item.value === this.value) {
        return item
      }
    }
    for (const group of this.groups) {
      for (const item of group.items) {
        if (item.value === this.value) {
          return item
        }
      }
    }
    return undefined
  }

  override render(): unknown {
    const selected = this.getSelectedItem()

    return html`
      <div>
        ${this.label ? html`<span class="trigger-label">${this.label}</span>` : ''}
        <button class="select-trigger ${this.open ? 'open' : ''}" @click=${this.toggleOpen}>
          <div class="trigger-content">
            ${selected?.icon ? html`<span class="trigger-icon">${selected.icon}</span>` : ''}
            ${selected
              ? html`<span class="trigger-text">${selected.label}</span>`
              : html`<span class="trigger-text trigger-placeholder">${this.placeholder}</span>`}
          </div>
          <span class="chevron ${this.open ? 'open' : ''}">expand_more</span>
        </button>

        ${this.open
          ? html`
              <div class="dropdown-panel">
                ${this.ungroupedItems.map(
                  item => html`
                    <div
                      class="option ${item.value === this.value ? 'selected' : ''}"
                      @click=${() => this.selectItem(item)}
                    >
                      ${item.icon
                        ? html`<span class="option-icon">${item.icon}</span>`
                        : html`<span class="option-placeholder-icon">—</span>`}
                      <span>${item.label}</span>
                    </div>
                  `
                )}
                ${this.ungroupedItems.length && this.groups.length
                  ? html`<div class="ungrouped-divider"></div>`
                  : ''}
                ${this.groups.map(group => {
                  const isCollapsed = this.collapsedGroups.has(group.label)
                  return html`
                    <div class="group-header" @click=${() => this.toggleGroup(group.label)}>
                      <span class="group-label">${group.label}</span>
                      <span class="group-chevron ${isCollapsed ? 'collapsed' : ''}"
                        >expand_more</span
                      >
                    </div>
                    ${!isCollapsed
                      ? group.items.map(
                          item => html`
                            <div
                              class="option ${item.value === this.value ? 'selected' : ''}"
                              @click=${() => this.selectItem(item)}
                            >
                              ${item.icon
                                ? html`<span class="option-icon">${item.icon}</span>`
                                : html`<span class="option-placeholder-icon">—</span>`}
                              <span>${item.label}</span>
                            </div>
                          `
                        )
                      : ''}
                  `
                })}
              </div>
            `
          : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'grouped-select': GroupedSelect
  }
}
